import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import { LocationFormModal, type LocationType, type CenterMeta } from "../components/locations/LocationFormModal";
import { CenterParamDrawer } from "../components/locations/CenterParamDrawer";
import { EquipmentAttrDrawer } from "../components/locations/EquipmentAttrDrawer";
import { FaultCatalogDrawer } from "../components/locations/FaultCatalogDrawer";
import { CopyEquipmentDrawer } from "../components/locations/CopyEquipmentDrawer";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { HardDeleteConfirm } from "../components/ui/HardDeleteConfirm";
import { Icon } from "../components/ui/Icon";
import { useIsMobile } from "../lib/useIsMobile";
import { Sheet } from "../components/mobile/primitives";
import "./Items.css";
import "./Locations.css";

const TYPE_LABELS: Record<LocationType, string> = {
  tesis: "Tesis",
  depo: "Depo",
  bolge: "Bölge",
  raf: "Raf",
  uretim_merkezi: "Merkez",
  uretim_hatti: "Hat",
  unite: "Ünite",
  makina: "Makina",
  mal_kabul: "Mal Kabul",
  sevkiyat: "Sevkiyat",
  karantina: "QC",
  scrap: "Scrap",
};

interface LocationRow {
  id: string;
  code: string;
  name: string;
  type: LocationType;
  parentId: string | null;
  isActive: boolean;
  productionCenter?: CenterMeta | null;
  _count?: { equipmentAttrs: number } | null;
}

interface TreeNode extends LocationRow {
  children: TreeNode[];
}

// Aynı seviyedeki sıralama: önce tip (merkezler üstte, depolar altta, karantina/scrap en altta), sonra kod.
const TYPE_ORDER: Record<LocationType, number> = {
  tesis: 0,
  uretim_merkezi: 1,
  uretim_hatti: 2,
  unite: 3,
  makina: 4,
  depo: 5,
  mal_kabul: 6,
  sevkiyat: 7,
  bolge: 8,
  raf: 9,
  karantina: 10,
  scrap: 11,
};

function buildTree(rows: LocationRow[]): TreeNode[] {
  const map = new Map<string, TreeNode>();
  rows.forEach((r) => map.set(r.id, { ...r, children: [] }));
  const roots: TreeNode[] = [];
  for (const node of map.values()) {
    if (node.parentId && map.has(node.parentId)) {
      map.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  // Her seviye: önce tip önceliği (TYPE_ORDER), aynı tipte kod sırası
  const sortRec = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => (TYPE_ORDER[a.type] ?? 99) - (TYPE_ORDER[b.type] ?? 99) || a.code.localeCompare(b.code));
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

export function LocationsPage() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const locsQ = trpc.location.list.useQuery();
  const toggleMut = trpc.location.toggleActive.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const regenMut = trpc.location.regenerateCodes.useMutation({ onSuccess: () => qc.invalidateQueries() });

  // Süper admin: çift onaylı kalıcı silme
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const isSuperAdmin = !!meQ.data?.user?.isSuperAdmin;
  const hardDeleteMut = trpc.location.hardDelete.useMutation();
  const [delTarget, setDelTarget] = useState<LocationRow | null>(null);
  function runHardDelete() {
    if (!delTarget) return;
    hardDeleteMut.mutate({ id: delTarget.id }, { onSuccess: () => { setDelTarget(null); hardDeleteMut.reset(); qc.invalidateQueries(); } });
  }

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<LocationRow | null>(null);
  const [presetParent, setPresetParent] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [expandInit, setExpandInit] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<null | { title: string; message: React.ReactNode; tone: "danger" | "primary"; confirmLabel: string; run: () => void }>(null);
  const [paramTarget, setParamTarget] = useState<{ id: string; name: string } | null>(null);
  const [attrTarget, setAttrTarget] = useState<{ id: string; name: string; kind: "unite" | "makina" } | null>(null);
  const [faultCatTarget, setFaultCatTarget] = useState<{ id: string; name: string } | null>(null);
  const [copySource, setCopySource] = useState<{ id: string; name: string; code: string; type: LocationType } | null>(null);

  const rows = useMemo(() => (locsQ.data ?? []) as unknown as LocationRow[], [locsQ.data]);
  const tree = useMemo(() => buildTree(rows), [rows]);

  // id → düğüm (children dahil) — detay kartı + üst lokasyon çözümü
  const nodeMap = useMemo(() => {
    const m = new Map<string, TreeNode>();
    const walk = (nodes: TreeNode[]) => nodes.forEach((n) => { m.set(n.id, n); walk(n.children); });
    walk(tree);
    return m;
  }, [tree]);

  // Varsayılan: ağaç KAPALI, yalnız tesis düğümleri açık — kullanıcı istediği dala iner.
  useEffect(() => {
    if (!expandInit && rows.length > 0) {
      setExpanded(new Set(rows.filter((r) => r.type === "tesis").map((r) => r.id)));
      setExpandInit(true);
    }
  }, [rows, expandInit]);

  // Seçili düğüm silindiyse seçimi bırak
  useEffect(() => {
    if (selectedId && rows.length > 0 && !nodeMap.has(selectedId)) setSelectedId(null);
  }, [nodeMap, selectedId, rows.length]);

  const selected = selectedId ? nodeMap.get(selectedId) ?? null : null;

  // Görünür satırlar (açık düğümlerin altı)
  const visibleRows = useMemo(() => {
    const out: Array<{ node: TreeNode; depth: number }> = [];
    const walk = (nodes: TreeNode[], depth: number) => {
      for (const n of nodes) {
        out.push({ node: n, depth });
        if (n.children.length > 0 && expanded.has(n.id)) walk(n.children, depth + 1);
      }
    };
    walk(tree, 0);
    return out;
  }, [tree, expanded]);

  // Seçili ünite/makina için atanmış özellikler
  const isEquip = selected?.type === "unite" || selected?.type === "makina";
  const attrsQ = trpc.equipmentAttr.list.useQuery(
    { locationId: selected?.id ?? "" },
    { enabled: !!selected && isEquip, staleTime: 10_000 },
  );

  function toggle(id: string) {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function openCreate(parentId: string | null) {
    setEditing(null);
    setPresetParent(parentId);
    setModalOpen(true);
  }
  function openEdit(row: LocationRow) {
    setEditing(row);
    setPresetParent(null);
    setModalOpen(true);
  }
  function handleToggle(row: LocationRow) {
    const newState = !row.isActive;
    setConfirm({
      title: newState ? "Lokasyonu aktifleştir?" : "Lokasyonu pasifleştir?",
      message: newState ? (
        <>
          <b>{row.code}</b> lokasyonunu tekrar aktifleştir?
        </>
      ) : (
        <>
          <b>{row.code}</b> lokasyonunu pasifleştir? Alt lokasyonlar etkilenmez.
        </>
      ),
      tone: newState ? "primary" : "danger",
      confirmLabel: newState ? "Aktifleştir" : "Pasifleştir",
      run: () => toggleMut.mutate({ id: row.id, isActive: newState }),
    });
  }

  function renderRow({ node, depth }: { node: TreeNode; depth: number }): JSX.Element {
    const hasChildren = node.children.length > 0;
    const isExpanded = expanded.has(node.id);
    const isSel = selectedId === node.id;
    return (
      <tr
        key={node.id}
        className={`loc-row type-${node.type} ${node.isActive ? "" : "inactive"} ${isSel ? "sel" : ""}`}
        onClick={() => setSelectedId(node.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedId(node.id); }
          if (e.key === "ArrowRight" && hasChildren && !isExpanded) toggle(node.id);
          if (e.key === "ArrowLeft" && hasChildren && isExpanded) toggle(node.id);
        }}
        tabIndex={0}
        aria-label={`${TYPE_LABELS[node.type]} ${node.code} ${node.name}`}
      >
        <td className="loc-cell-main">
          <div className="loc-cell" style={{ paddingLeft: depth * 18 }}>
            <button
              type="button"
              className={`twist ${hasChildren ? "" : "empty"} ${isExpanded ? "expanded" : ""}`}
              onClick={(e) => { e.stopPropagation(); if (hasChildren) toggle(node.id); }}
              aria-label={isExpanded ? "Daralt" : "Genişlet"}
              aria-expanded={hasChildren ? isExpanded : undefined}
              tabIndex={hasChildren ? 0 : -1}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m9 6 6 6-6 6" />
              </svg>
            </button>
            <span className={`type-chip ${node.type}`}>{TYPE_LABELS[node.type]}</span>
            <span className="code">{node.code}</span>
            <span className="name">{node.name}</span>
          </div>
        </td>
        <td className="loc-cell-meta">
          {node.type === "uretim_merkezi" && node.productionCenter?.dailyCapacity != null ? (
            <span className="badge-pill b-info" title="Günlük kapasite">
              {Number(node.productionCenter.dailyCapacity).toLocaleString("tr-TR")} {node.productionCenter.capacityUnit ?? ""}
            </span>
          ) : null}
          {(node.type === "unite" || node.type === "makina") && (node._count?.equipmentAttrs ?? 0) > 0 ? (
            <span className="badge-pill b-info" title="Tanımlı özellik sayısı">
              {node._count!.equipmentAttrs} özellik
            </span>
          ) : null}
        </td>
        <td className="cell-num">{hasChildren ? node.children.length : ""}</td>
        <td>
          {node.isActive ? (
            <span className="badge-pill b-success">Aktif</span>
          ) : (
            <span className="badge-pill">Pasif</span>
          )}
        </td>
      </tr>
    );
  }

  // ── Detay kartı içeriği ──
  function renderDetail(node: TreeNode): JSX.Element {
    const parent = node.parentId ? nodeMap.get(node.parentId) ?? null : null;
    const hasChildren = node.children.length > 0;
    const pc = node.type === "uretim_merkezi" ? node.productionCenter : null;
    return (
      <div className="d-card">
        <div className="d-card-head">
          <div className="left">
            <span className="t">{node.name}</span>
            <span className="s">{node.code}</span>
          </div>
          <span className={`type-chip ${node.type}`}>{TYPE_LABELS[node.type]}</span>
        </div>
        <div className="d-card-body tight">
          <div className="loc-facts">
            <div className="loc-fact">
              <span className="lbl">Durum</span>
              <span className="val">
                {node.isActive ? <span className="badge-pill b-success">Aktif</span> : <span className="badge-pill">Pasif</span>}
              </span>
            </div>
            <div className="loc-fact">
              <span className="lbl">Üst Lokasyon</span>
              <span className="val">
                {parent ? (<><span className="mono">{parent.code}</span> · {parent.name}</>) : "— (kök)"}
              </span>
            </div>
            <div className="loc-fact">
              <span className="lbl">Alt Konum</span>
              <span className="val mono">{node.children.length}</span>
            </div>
            {pc ? (
              <>
                {pc.dailyCapacity != null ? (
                  <div className="loc-fact">
                    <span className="lbl">Günlük Kapasite</span>
                    <span className="val mono">{Number(pc.dailyCapacity).toLocaleString("tr-TR")} {pc.capacityUnit ?? ""}</span>
                  </div>
                ) : null}
                {pc.shiftsPerDay != null ? (
                  <div className="loc-fact">
                    <span className="lbl">Vardiya / Gün</span>
                    <span className="val mono">{pc.shiftsPerDay}{pc.hoursPerShift != null ? ` × ${Number(pc.hoursPerShift).toLocaleString("tr-TR")} sa` : ""}</span>
                  </div>
                ) : null}
                {pc.performancePct != null ? (
                  <div className="loc-fact">
                    <span className="lbl">Performans</span>
                    <span className="val mono">%{Number(pc.performancePct).toLocaleString("tr-TR")}</span>
                  </div>
                ) : null}
                {pc.maxConcurrentWo != null ? (
                  <div className="loc-fact">
                    <span className="lbl">Eşzamanlı İş Emri</span>
                    <span className="val mono">{pc.maxConcurrentWo}</span>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>

          {isEquip ? (
            <div className="loc-attrs">
              <div className="loc-sec-title">Atanmış Özellikler</div>
              {attrsQ.isLoading ? (
                <p className="loc-muted">Yükleniyor…</p>
              ) : (attrsQ.data?.length ?? 0) === 0 ? (
                <p className="loc-muted">Özellik tanımlı değil.</p>
              ) : (
                <div className="loc-attr-list">
                  {attrsQ.data!.map((a) => (
                    <div className="loc-fact" key={a.id}>
                      <span className="lbl">{a.label}</span>
                      <span className="val mono">{a.value}{a.unit ? ` ${a.unit}` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          <div className="loc-actions">
            <button type="button" className="btn-md secondary" onClick={() => openEdit(node)}>
              <Icon name="edit" size={14} /> Düzenle
            </button>
            {node.type !== "makina" ? (
              <button type="button" className="btn-md secondary" onClick={() => openCreate(node.id)}>
                <Icon name="plus" size={14} /> Alt Lokasyon
              </button>
            ) : null}
            {node.type === "unite" || node.type === "makina" ? (
              <button type="button" className="btn-md secondary" onClick={() => setAttrTarget({ id: node.id, name: node.name, kind: node.type as "unite" | "makina" })}>
                <Icon name="list2" size={14} /> Özellikler
              </button>
            ) : null}
            {node.type === "uretim_merkezi" ? (
              <>
                <button type="button" className="btn-md secondary" onClick={() => setParamTarget({ id: node.id, name: node.name })}>
                  <Icon name="list2" size={14} /> Parametreler
                </button>
                <button type="button" className="btn-md secondary" onClick={() => setFaultCatTarget({ id: node.id, name: node.name })}>
                  <Icon name="search" size={14} /> Arıza Kataloğu
                </button>
              </>
            ) : null}
            {hasChildren ? (
              <button
                type="button"
                className="btn-md secondary"
                title="Alt konumların kodlarını yeni (kısa) formata göre yeniden üret"
                disabled={regenMut.isPending}
                onClick={() => setConfirm({
                  title: "Kodları yenile",
                  message: (
                    <>
                      <b>{node.code}</b> altındaki <b>tüm konumların</b> kodları yeni (kısa) formata göre yeniden üretilecek.
                      Bu düğümün kendi kodu ({node.code}) korunur. Kodlar hiçbir kayıtla bağlı değildir; işlem güvenlidir ama geri alınamaz.
                    </>
                  ),
                  tone: "primary",
                  confirmLabel: "Kodları Yenile",
                  run: () => regenMut.mutate({ id: node.id }),
                })}
              >
                <Icon name="refresh" size={14} /> Kodları Yenile
              </button>
            ) : null}
            {isSuperAdmin && (node.type === "uretim_hatti" || node.type === "uretim_merkezi") && hasChildren ? (
              <button
                type="button"
                className="btn-md secondary"
                title="Ünite/makina ağacını başka hatta/merkeze kopyala (süper admin)"
                onClick={() => setCopySource({ id: node.id, name: node.name, code: node.code, type: node.type })}
              >
                <Icon name="copy" size={14} /> Ekipmanı Kopyala
              </button>
            ) : null}
            <button
              type="button"
              className={`btn-md secondary ${node.isActive ? "tone-danger" : "tone-success"}`}
              onClick={() => handleToggle(node)}
              disabled={toggleMut.isPending}
            >
              <Icon name={node.isActive ? "archive" : "check"} size={14} /> {node.isActive ? "Pasifleştir" : "Aktifleştir"}
            </button>
            {isSuperAdmin ? (
              <button
                type="button"
                className="btn-md secondary tone-danger"
                title="Kalıcı sil (süper admin)"
                onClick={() => { hardDeleteMut.reset(); setDelTarget(node); }}
              >
                <Icon name="trash" size={14} /> Kalıcı Sil
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  const drawers = (
    <>
      <LocationFormModal
        open={modalOpen}
        initial={editing}
        presetParentId={presetParent}
        onClose={() => setModalOpen(false)}
      />

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title ?? ""}
        message={confirm?.message}
        tone={confirm?.tone ?? "primary"}
        confirmLabel={confirm?.confirmLabel ?? "Onayla"}
        cancelLabel="Vazgeç"
        busy={toggleMut.isPending || regenMut.isPending}
        onConfirm={() => { const c = confirm; setConfirm(null); c?.run(); }}
        onCancel={() => setConfirm(null)}
      />

      <HardDeleteConfirm
        open={!!delTarget}
        code={delTarget?.code ?? ""}
        title="Lokasyonu kalıcı sil"
        warning={
          <>
            <b>{delTarget?.code}</b> — {delTarget?.name} lokasyonunu <b>KALICI</b> olarak sileceksiniz.
            Alt lokasyon, stok hareketi/bakiyesi, üretim partisi, sevkiyat veya rezervasyon varsa
            silme engellenir.
          </>
        }
        busy={hardDeleteMut.isPending}
        errorMessage={hardDeleteMut.error?.message ?? null}
        onConfirmed={runHardDelete}
        onClose={() => { setDelTarget(null); hardDeleteMut.reset(); }}
      />

      <CenterParamDrawer
        open={!!paramTarget}
        centerId={paramTarget?.id ?? ""}
        centerName={paramTarget?.name ?? ""}
        onClose={() => setParamTarget(null)}
      />

      <EquipmentAttrDrawer
        open={!!attrTarget}
        locationId={attrTarget?.id ?? ""}
        locationName={attrTarget?.name ?? ""}
        kind={attrTarget?.kind}
        onClose={() => setAttrTarget(null)}
      />

      <FaultCatalogDrawer
        open={!!faultCatTarget}
        centerId={faultCatTarget?.id ?? ""}
        centerName={faultCatTarget?.name ?? ""}
        onClose={() => setFaultCatTarget(null)}
      />

      <CopyEquipmentDrawer
        open={!!copySource}
        source={copySource}
        onClose={() => setCopySource(null)}
      />
    </>
  );

  if (isMobile) {
    return (
      <>
        <div className="m-stickytop">
          <button type="button" className="btn-md primary" style={{ justifyContent: "center" }} onClick={() => openCreate(null)}><Icon name="plus" size={15} />Yeni Lokasyon (Kök)</button>
          <div className="m-ovl" style={{ padding: "0 2px" }}>Tesis hiyerarşisi · <b className="mono">{locsQ.data?.length ?? 0}</b> konum</div>
        </div>

        {locsQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : visibleRows.length === 0 ? (
          <div className="m-empty">Henüz lokasyon yok — ilk tesis/depo kaydını ekleyin.</div>
        ) : (
          <div className="m-card" style={{ overflow: "hidden" }}>
            {visibleRows.map(({ node, depth }, i) => {
              const hasChildren = node.children.length > 0;
              const isExpanded = expanded.has(node.id);
              return (
                <div
                  key={node.id}
                  onClick={() => setSelectedId(node.id)}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", paddingLeft: 12 + depth * 16, borderTop: i > 0 ? "1px solid var(--neutral-100)" : undefined, cursor: "pointer", opacity: node.isActive ? 1 : 0.55 }}
                >
                  {hasChildren ? (
                    <button type="button" onClick={(e) => { e.stopPropagation(); toggle(node.id); }} aria-label={isExpanded ? "Daralt" : "Genişlet"} style={{ background: "transparent", border: 0, padding: 2, display: "flex", color: "var(--neutral-400)", cursor: "pointer" }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ transform: isExpanded ? "rotate(90deg)" : "none", transition: "transform .15s" }} aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>
                    </button>
                  ) : <span style={{ width: 17, flexShrink: 0 }} />}
                  <span className={`type-chip ${node.type}`} style={{ flexShrink: 0 }}>{TYPE_LABELS[node.type]}</span>
                  <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                    <span className="mono" style={{ fontSize: 12, fontWeight: 700, color: "var(--neutral-900)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.code}</span>
                    <span style={{ fontSize: 11.5, color: "var(--neutral-500)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{node.name}</span>
                  </span>
                  {!node.isActive ? <span className="badge b-neutral no-dot" style={{ flexShrink: 0 }}>Pasif</span> : null}
                  {hasChildren ? <span className="mono" style={{ fontSize: 11, color: "var(--neutral-400)", flexShrink: 0 }}>{node.children.length}</span> : null}
                </div>
              );
            })}
          </div>
        )}

        <Sheet open={!!selected} title={selected ? `${selected.code} · ${selected.name}` : ""} onClose={() => setSelectedId(null)}>
          {selected ? renderDetail(selected) : null}
        </Sheet>

        {drawers}
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Lokasyonlar</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Lokasyonlar</h1>
          <p className="sub">Tesis hiyerarşisi · {locsQ.data?.length ?? 0} konum</p>
        </div>
        <button type="button" className="btn primary" onClick={() => openCreate(null)}>
          + Yeni Lokasyon (Kök)
        </button>
      </div>

      <div className="loc-layout">
        <div className="dt2-wrap">
          <div className="dt2-scroll loc-scroll">
            <table className="dt2 loc-table">
              <thead>
                <tr>
                  <th>Lokasyon</th>
                  <th>Detay</th>
                  <th className="num">Alt</th>
                  <th>Durum</th>
                </tr>
              </thead>
              <tbody>
                {locsQ.isLoading ? (
                  <tr><td colSpan={4}><div className="empty-state"><p className="ttl">Yükleniyor…</p></div></td></tr>
                ) : visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={4}>
                      <div className="empty-state">
                        <p className="ttl">Henüz lokasyon yok</p>
                        <p className="sub">İlk tesis/depo kaydını ekleyin.</p>
                        <button type="button" className="btn-md primary" style={{ marginTop: 12 }} onClick={() => openCreate(null)}>
                          + Yeni Lokasyon
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  visibleRows.map(renderRow)
                )}
              </tbody>
            </table>
          </div>
        </div>

        <aside className="loc-detail">
          {selected ? (
            renderDetail(selected)
          ) : (
            <div className="d-card">
              <div className="d-card-body loc-detail-empty">
                <Icon name="mapPin" size={22} />
                <p className="ttl">Lokasyon seçin</p>
                <p className="sub">Soldaki ağaçta bir satıra tıklayın — bilgiler, atanmış özellikler ve işlemler burada gösterilir.</p>
              </div>
            </div>
          )}
        </aside>
      </div>

      {drawers}
    </div>
  );
}
